import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, ArrowLeft, ShieldCheck, Clock, Award } from "lucide-react";
import { Navbar } from "../components/sections/Navbar";
import { Footer } from "../components/sections/Footer";
import useReveal from "../lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const orgTypes = [
    { value: "school", label: "School / University" },
    { value: "nightlife", label: "Nightlife Venue" },
    { value: "community", label: "Community Organization" },
    { value: "municipal", label: "Municipal Facility" },
    { value: "workplace", label: "Workplace" },
    { value: "healthcare", label: "Healthcare System" },
    { value: "correctional", label: "Correctional Facility" },
    { value: "public", label: "Public Gathering Space" },
    { value: "other", label: "Other" },
];

const highlights = [
    { icon: Clock, label: "90-day pilot" },
    { icon: ShieldCheck, label: "$0 cost to partner" },
    { icon: Award, label: "No long-term commitment" },
];

const initial = {
    full_name: "",
    organization: "",
    organization_type: "school",
    role: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    foot_traffic: "",
    timeline: "",
    environment: "",
};

export default function PilotApplication() {
    useReveal();
    const navigate = useNavigate();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (
            !form.full_name ||
            !form.organization ||
            !form.email ||
            form.environment.length < 10
        ) {
            toast.error("Please complete the required fields.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/pilot-application`, form);
            setDone(true);
            toast.success("Application received. We'll be in touch shortly.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="pilot-application-page" className="min-h-screen bg-white">
            <Navbar />

            {/* Header band */}
            <section className="relative bg-[#0A1428] text-white pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
                <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                <div className="absolute -top-32 -right-24 w-[520px] h-[520px] opacity-[0.08] pointer-events-none float-slow">
                    <svg viewBox="0 0 200 200">
                        <polygon points="20,160 130,60 180,160" fill="#F0493E" />
                    </svg>
                </div>

                <div className="relative max-w-4xl mx-auto px-6 md:px-12">
                    <Link
                        to="/"
                        data-testid="pilot-back-link"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-slate-300 hover:text-white transition-colors mb-10"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Back to Kairos
                    </Link>

                    <div className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Pilot Program Application
                    </div>
                    <h1 className="mt-4 font-display font-extrabold text-4xl md:text-6xl leading-[1] tracking-tighter text-balance">
                        Bring Kairos ORD
                        <br />
                        to your organization.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-300 font-medium leading-relaxed">
                        Tell us about your facility. A member of our team will
                        follow up within one business day to discuss whether
                        your organization is a good fit for the pilot program.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        {highlights.map((h) => (
                            <div
                                key={h.label}
                                className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-sm"
                            >
                                <h.icon
                                    size={14}
                                    strokeWidth={1.5}
                                    className="text-[#F0493E]"
                                />
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-white">
                                    {h.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="relative bg-slate-50 py-16 md:py-24 border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    {done ? (
                        <div
                            data-testid="pilot-success"
                            className="reveal bg-white border border-slate-200 p-10 md:p-16 text-center"
                        >
                            <div className="relative inline-grid h-20 w-20 place-items-center bg-[#F0493E] rounded-full">
                                <span className="absolute inset-0 rounded-full pulse-ring" />
                                <CheckCircle2
                                    size={40}
                                    strokeWidth={1.5}
                                    className="text-white relative"
                                />
                            </div>
                            <h2 className="mt-8 font-display font-extrabold text-3xl md:text-5xl text-[#0A1428] tracking-tighter">
                                Application received.
                            </h2>
                            <p className="mt-6 max-w-lg mx-auto text-base md:text-lg text-slate-600 font-medium leading-relaxed">
                                Thank you for stepping up. A member of the
                                Kairos ORD team will review your application
                                and reach out within one business day to
                                discuss next steps.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
                                <button
                                    data-testid="pilot-success-home"
                                    onClick={() => navigate("/")}
                                    className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                                >
                                    Back to home
                                    <ArrowRight size={18} strokeWidth={2} />
                                </button>
                                <button
                                    data-testid="pilot-success-reset"
                                    onClick={() => {
                                        setForm(initial);
                                        setDone(false);
                                    }}
                                    className="btn-ghost inline-flex items-center justify-center px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                                >
                                    Submit another
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form
                            data-testid="pilot-form"
                            onSubmit={submit}
                            className="reveal bg-white border border-slate-200 p-8 md:p-12"
                        >
                            <FormGroup title="About you">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Full name *">
                                        <input
                                            data-testid="pilot-input-name"
                                            required
                                            value={form.full_name}
                                            onChange={onChange("full_name")}
                                            placeholder="Jane Doe"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Role / Title">
                                        <input
                                            data-testid="pilot-input-role"
                                            value={form.role}
                                            onChange={onChange("role")}
                                            placeholder="Director of Operations"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Email *">
                                        <input
                                            data-testid="pilot-input-email"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={onChange("email")}
                                            placeholder="jane@org.gov"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Phone">
                                        <input
                                            data-testid="pilot-input-phone"
                                            type="tel"
                                            value={form.phone}
                                            onChange={onChange("phone")}
                                            placeholder="(555) 123-4567"
                                            className="kairos-input"
                                        />
                                    </Field>
                                </div>
                            </FormGroup>

                            <FormGroup title="Your organization" className="mt-10">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Organization name *">
                                        <input
                                            data-testid="pilot-input-org"
                                            required
                                            value={form.organization}
                                            onChange={onChange("organization")}
                                            placeholder="Springfield High School"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Organization type *">
                                        <select
                                            data-testid="pilot-input-orgtype"
                                            value={form.organization_type}
                                            onChange={onChange(
                                                "organization_type"
                                            )}
                                            className="kairos-input kairos-select pr-10"
                                        >
                                            {orgTypes.map((o) => (
                                                <option
                                                    key={o.value}
                                                    value={o.value}
                                                >
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="City">
                                        <input
                                            data-testid="pilot-input-city"
                                            value={form.city}
                                            onChange={onChange("city")}
                                            placeholder="Springfield"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="State">
                                        <input
                                            data-testid="pilot-input-state"
                                            value={form.state}
                                            onChange={onChange("state")}
                                            placeholder="IL"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Est. foot traffic / people served">
                                        <input
                                            data-testid="pilot-input-traffic"
                                            value={form.foot_traffic}
                                            onChange={onChange("foot_traffic")}
                                            placeholder="~2,000 students / day"
                                            className="kairos-input"
                                        />
                                    </Field>
                                    <Field label="Ideal timeline">
                                        <input
                                            data-testid="pilot-input-timeline"
                                            value={form.timeline}
                                            onChange={onChange("timeline")}
                                            placeholder="Next 30–60 days"
                                            className="kairos-input"
                                        />
                                    </Field>
                                </div>
                            </FormGroup>

                            <FormGroup title="Tell us about your environment" className="mt-10">
                                <Field label="Environment & motivation *">
                                    <textarea
                                        data-testid="pilot-input-environment"
                                        required
                                        value={form.environment}
                                        onChange={onChange("environment")}
                                        rows={6}
                                        placeholder="Where would units be placed? What overdose response challenges have you seen? Anything else we should know?"
                                        className="kairos-input resize-none"
                                    />
                                </Field>
                            </FormGroup>

                            <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <p className="text-xs text-slate-500 font-medium max-w-md">
                                    By submitting this application you agree
                                    to be contacted by Kairos ORD regarding
                                    the pilot program. We never share your
                                    information.
                                </p>
                                <button
                                    data-testid="pilot-form-submit"
                                    disabled={submitting}
                                    type="submit"
                                    className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm disabled:opacity-60"
                                >
                                    {submitting
                                        ? "Sending…"
                                        : "Submit application"}
                                    <ArrowRight size={18} strokeWidth={2} />
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                <style>{`
                    .kairos-input {
                        width: 100%;
                        background: #ffffff;
                        border: 1px solid #E2E8F0;
                        border-radius: 2px;
                        padding: 14px 16px;
                        font-size: 15px;
                        font-family: 'Manrope', sans-serif;
                        color: #0A1428;
                        transition: border-color .2s ease, box-shadow .2s ease;
                    }
                    .kairos-input::placeholder { color: #94a3b8; }
                    .kairos-input:focus {
                        outline: none;
                        border-color: #0A1428;
                        box-shadow: 0 0 0 3px rgba(240,73,62,0.18);
                    }
                    .kairos-select {
                        appearance: none;
                        -webkit-appearance: none;
                        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M2 4l4 4 4-4' stroke='%230A1428' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>");
                        background-repeat: no-repeat;
                        background-position: right 1rem center;
                        background-size: 12px;
                    }
                `}</style>
            </section>

            <Footer />
        </main>
    );
}

const FormGroup = ({ title, children, className = "" }) => (
    <div className={className}>
        <div className="flex items-center gap-4 mb-6">
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                {title}
            </span>
            <span className="flex-1 h-px bg-slate-200" />
        </div>
        {children}
    </div>
);

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">
            {label}
        </span>
        {children}
    </label>
);
